export const objective = 'Seeking a remote Staff or Principal engineering role building reliable distributed systems and applied AI platforms, bringing experience in high-throughput pricing, payments, autonomous agents, and leadership across engineering teams.'

export const employment = [
  {
    'start-date': 'August 2022',
    'end-date': undefined,
    title: 'Staff Software Engineer',
    company: 'Affirm, Inc.',
    location: 'Remote',
    responsibilities: [
      'Led recovery efforts for a stalled pricing-configuration project, aligning 13 teams around a shared goal',
      'Developed Libretto, an autonomous note-taking swarm with PARA-based memory, custom collectors, and agent lifecycle management; project-organized streams in a local Obsidian Markdown library reduced tokens per request by an average of 40% in testing',
      'Invited on several occasions to present as a leading voice in AI and autonomous development to an audience of over 1,000 developers',
      'Tech used: Python (Flask/SQLAlchemy), Databricks, Docker, Kubernetes, Airflow, ReactJS',
      'Worked to enhance quality of back-end machine learning pipelines',
      'Held architectural oversight for the pricing service, which averages a sustained 12 million requests per minute at 99.999% measured availability tracked using Chronosphere and service metrics, while driving team quality and development practices to maximize delivery reliability',
      'Won Affirm’s company hackathon as part of a team and helped ship one of the company’s first production LLM integrations',
      'Participated in the redesign of the software engineering interview process by developing a new screening question',
      'Provided mentorship and guidance to team members, while also working to level up the larger engineering organization',
      'Created and collaborated on many software designs, gathering feedback and buy-in before driving projects through development to delivery',
      'Improved development pipelines and reduced process inefficiencies to improve concurrent experimentation capacity by 400%',
      'Developed automated reporting to track financing program experimentation and configuration changes, supporting business needs',
    ],
  },
  {
    'start-date': 'June 2021',
    'end-date': 'April 2022',
    title: 'Staff Software Development Engineer',
    company: 'Recharge Payments',
    location: 'Remote',
    responsibilities: [
      'Tech used: Python (Flask/SQLAlchemy), Ruby (Rails), React, Google Cloud Platform, Shopify API',
      'Worked across multiple tech stacks in a cross-functional role with a focus on scalability and resiliency',
      'Established a technical design process to improve predictability of delivery and reduce bugs',
      'Worked with engineers to shepherd a complex technical workflows engine from a Redis/Sidekiq base to one built on top of Temporal and Go',
      'Mentored multiple engineers successfully while serving as a team lead',
      'Was routinely praised for my communication ability and professionalism',
    ],
  },
  {
    'start-date': 'September 2019',
    'end-date': 'June 2021',
    title: 'Staff Software Engineer',
    company: 'Chegg, Inc.',
    location: 'Remote',
    responsibilities: [
      'Tech used: Python (Flask/SQLAlchemy), AWS Lambda, AWS SAM, Salesforce, Kotlin',
      'As a staff engineer, I served as a central knowledge touchpoint for the lead processing system for the Thinkful property',
      'Mentored multiple engineers successfully while serving as a team lead',
      'Introduced a software design process that allowed the team to create a predictable velocity, allowing business partners to more effectively work alongside engineering',
      'Designed multiple systems and oversaw their progress from execution to completion',
      'Championed new technology and helped to evangelize patterns throughout the engineering organization',
      'Developed standards for code quality and ensured they were adhered to',
      'Learned new languages and techniques as needed to meet business goals',
      'Participated heavily in redesign of legacy monolith codebases into a microservice architecture',
    ],
  },
  {
    'start-date': 'May 2019',
    'end-date': 'September 2019',
    title: 'Senior Software Engineer',
    company: 'StyleSeat',
    location: 'Remote',
    responsibilities: [
      'Tech used: Python (Django), React, Elasticsearch',
      'Developed and delivered multiple features on time and under-budget',
      'Helped enhance code review standards, and served as a champion of unit testing',
    ],
  },
  {
    'start-date': 'October 2017',
    'end-date': 'May 2019',
    title: 'Chapter Manager, Platform',
    company: 'Sprout Social',
    location: 'Chicago, IL',
    responsibilities: [
      'Tech used: Python (Django), Java (Dropwizard), NodeJS, MySQL, AWS, Rackspace, Ruby (Rails)',
      'As an engineering manager, I served as direct supervisor to multiple engineers, guiding their performance and resolving conflicts while continuing to serve as a mentor to the engineering organization.',
      'Served successfully in a multidisciplinary role, balancing development deliverables with the needs of my reports while contributing to multiple org-wide programs and training initiatives',
      'Developed and delivered multiple sessions on unit testing, providing critical software engineering education.',
      'Successfully delivered on multiple projects with very short timelines, including GDPR remediation, a significant Rails upgrade that was completed in under a week (2.0 to 5.1), and taking over multiple orphaned edge services, bringing them to a modernized operational status (chef deployment, logstash aggregation, etc)',
      'Wrote production quality code in multiple languages, having picked up two while working on the product, for services that continue to operate efficiently and effectively.',
      'Successfully migrated 1,200 customers billed manually in Oracle Netsuite to a Recurly instance, significantly reducing back-office payment processing costs.',
    ],
  },
  {
    'start-date': 'January 2017',
    'end-date': 'October 2017',
    title: 'Director of Product Management',
    company: 'Backand',
    location: 'Tel Aviv/Remote',
    responsibilities: [
      'Tech used: Node.JS, JavaScript, AngularJS, AWS Lambda, Microsoft Azure Functions, Google Cloud Functions, Amazon Alexa development, Serverless architectures and development',
      'Successfully managed inception, development, and launch of multiple customer-facing products',
      'Worked extensively with customers to identify pain points in the system, translating these needs and driving engineering implementation',
      'Successfully managed a distributed development team, ensuring all developers were fully utilized while also having full knowledge of the business reasons driving their tasks',
      'Served as the public face of the company, presenting at conferences and meetups throughout the US',
      'Performed developer evangelism via blogging, technical documentation, and direct community outreach',
    ],
  },
  {
    'start-date': 'March 2015',
    'end-date': 'January 2017',
    title: 'Principal Software Engineer',
    company: 'Invoice2go',
    location: 'Redwood City, CA',
    responsibilities: [
      'Tech used: Ruby on Rails, Postgres, nginx, AngularJS, CircleCI, Git, Sidekiq, Capistrano',
      'Promoted from Lead Engineer due to repeated successful development projects',
      'Successfully managed a distributed development team, with team members located in Redwood City (California), Jakarta (Indonesia), and Sydney (Australia)',
      'Delivered multiple complex projects and integrations on time and under-budget, handling shifting requirements and business needs in a dynamic high-performance environment',
      'Directed every facet of the product development process, from product design through production deployment and maintenance',
      'Used established expertise in TDD to develop a system with zero production defects',
      'Built a payments system that served as the primary revenue driver for the organization, handling $8,000,000 USD in payment processing each week with 99.999% uptime',
      'Successfully integrated with multiple payment vendors in a multi-tenant payments platform with stringent response requirements',
      'Was routinely praised for technical expertise, architectural prowess, and quality of deliverables',
    ],
  },
  {
    'start-date': 'December 2013',
    'end-date': 'March 2015',
    title: 'Senior Software Engineer',
    company: 'Recsolu/Yello',
    location: 'Chicago, IL/Remote',
    responsibilities: [
      'Tech used: Ruby on Rails, Postgres, JavaScript, Capistrano, Bitbucket',
      'Worked with Amazon S3 to implement resume bundling functionality for event attendees',
      'Cleaned up and organized 700+ unit tests, enhancing team code quality',
      'Worked successfully in a remote capacity, maintaining a high level of productivity and visibility',
      'Helped drive design decisions and highlight architectural improvements to existing Ruby applications',
    ],
  },
  {
    'start-date': 'August 2012',
    'end-date': 'December 2013',
    title: 'Senior Software Engineer',
    company: 'Enova Financial',
    location: 'Chicago, IL',
    responsibilities: [
      'Tech used: Ruby on Rails, JavaScript, SOAP, REST APIs, Git, Rspec, Cucumber',
      'Received repeated and consistent praise on clarity of communications and project status updates',
      'Was consistently recognized for tenacity, detail orientation, and exemplary work ethic in pursuing solutions',
      'Implemented a fraud sampling application that allowed analysts to perform A/B testing on potential customers, increasing fraud detection capabilities and increasing revenue',
      'Implemented the Yodlee Financial Services API to provide account verification for new customers, reducing fraud and identifying additional customers that would have otherwise been dismissed',
      'Served as a lead developer for an in-house application that localized all common IT maintenance functionality into a single web interface',
      'Served as lead developer on functionality adding additional payment providers to the website, enhancing site stability and reducing business risk',
    ],
  },
  {
    'start-date': 'August 2011',
    'end-date': 'August 2012',
    title: 'Software Engineer',
    company: 'CPM Marketing Group',
    location: 'Madison, WI',
    responsibilities: [
      'Tech used: ColdFusion, JavaScript, Postgres, SVN',
      'Implemented a high performance mass emailing application back end using Amazon SES, Java, and PostgreSQL',
      'Proposed, developed, and implemented a code review process resulting in a decrease in shipped defects',
      'Implemented new functionality using Google Maps API that assisted users in accomplishing their goals',
      'Consistently shipped low-defect solutions to numerous business problems while helping to revise the software development life cycle to reduce redundancy',
    ],
  },
{
  "start-date": "March 2010",
  "end-date": "August 2011",
  "title": "Senior Gaming Software Engineer",
  "company": "WMS Gaming, Inc.",
  "location": "Chicago, IL",
  "responsibilities": [
    "Tech used: ActionScript 3.0, C++, SVN",
    "Shipped titles: Hearts of Venice, Griffin’s Gate, I Love Lucy, All That Glitters 2, Plataea",
    "Helped set strategy and objectives for new code artifacts through code reviews and design meetings",
    "Developed new systems within abbreviated delivery timelines"
  ]
},
{
  "start-date": "August 2009",
  "end-date": "March 2010",
  "title": "Engineer",
  "company": "Trading Technologies",
  "location": "Chicago, IL",
  "responsibilities": [
    "Developed a multithreaded messaging system for application permissions management"
  ]
},
{
  "start-date": "February 2009",
  "end-date": "August 2009",
  "title": "Software Engineer",
  "company": "Enova Financial",
  "location": "Chicago, IL",
  "responsibilities": [
    "Implemented credit reports using SOAP and Ruby to support fraud reduction",
    "Documented existing functionality to support onboarding",
    "Served as a development lead for offshore resources and a technical resource for project managers"
  ]
},
{
  "start-date": "August 2007",
  "end-date": "January 2009",
  "title": "Graphics Programmer",
  "company": "Raw Thrills, Inc.",
  "location": "Skokie, IL",
  "responsibilities": [
    "Implemented graphical effects in a DirectX 9 rendering engine",
    "Ported a game engine from DirectX 8 to DirectX 9",
    "Built a reloadable shader debugger in HLSL and Cg"
  ]
},
{
  "start-date": "May 2006",
  "end-date": "August 2007",
  "title": "Gaming Software Engineer",
  "company": "WMS Gaming, Inc.",
  "location": "Chicago, IL",
  "responsibilities": []
},
{
  "start-date": "August 2004",
  "end-date": "May 2006",
  "title": "Application Developer Specialist",
  "company": "Allstate Insurance Co.",
  "location": "Northbrook, IL",
  "responsibilities": []
}
]

export const education = [
  {
    school: 'DePaul University',
    degree: 'Master of Science',
    major: 'Computer Graphics and Animation (developer concentration)',
    graduationDate: '06/2007',
    city: 'Chicago, IL',
    notes: 'Graduated with distinction. Member of Upsilon Pi Epsilon Computer Science Honor Society',
  },
  {
    school: 'Iowa State University',
    degree: 'Bachelor of Science',
    major: 'Computer Science',
    graduationDate: '12/2003',
    city: 'Ames, IA',
    notes: 'Minor in Philosophy. Member of the ISUCF"V"MB, Leader of the Hockey Pep Band, Member of the ISU Karate Club, Member of the ISU Game Development Club, Member of Tau Beta Sigma',
  },
]

export const otherRelated = [
  {
    data: 'North American Brass Band Association',
    children: ['Board member, 2022-2024', 'Tech lead, 2022-2024'],
  },
  {
    data: 'Chicago Brass Band',
    children: [
      'President, 2025',
      'Vice President, 2024',
      'Board member, 2023',
      'First Baritone, 2025',
      'Solo Tenor Horn, 2024',
      'First Tenor Horn, 2019-2024',
    ],
  },
  {
    data: 'Kilties Drum and Bugle Corps',
    children: [
      'Program Coordinator, 2019',
      'Visual Caption Head, 2018',
      'Visual Tech, 2009, 2011-2012',
      'Mellophone, 2014',
      'Baritone, 2007-2009',
    ],
  },
  {
    data: 'Illinois Brass Band',
    children: [
      'Tenor horn, 2009-2011, 2014, 2017-2018',
      '2nd Baritone, 2010',
      '2nd Cornet, 2013',
      'Board member at large, 2010',
    ],
  },
  {
    data: 'DeVry University, Visiting Professor 2011-2018',
    children: [
      'Taught Computer Graphics Programming 1 and 2, Math Programming for Games, and Introduction to Game and Simulation Programming',
    ],
  },
  {
    data: 'Southern New Hampshire University, Adjunct Faculty 2012-2017',
    children: [
      'Taught Introduction to Software Development and Interactive 3D Virtual Environments',
    ],
  },
  {
    data: 'Rasmussen College, Adjunct Faculty, Subject Matter Expert 2012-2015',
    children: [
      'Taught Game Design Theory 1 and 2, and DirectX Lighting',
      'Designed multiple courses in computer graphics development',
    ],
  },
  { data: 'IGDA Scholarship Recipient', children: [] },
  { data: 'Upsilon Pi Epsilon Computer Science Honor Society', children: [] },
]
